import { Fragment } from "react";
import { F } from "../theme/tokens";

/**
 * Barra dei passi del flusso di anonimizzazione.
 *
 * I cinque passi rispecchiano gli stati che AnonymizePage ha gia': non ne
 * introducono di nuovi, quindi la barra non puo' raccontare una cosa diversa
 * da quella che l'utente sta vedendo.
 *
 * "Acquista" ha un passo suo invece di stare dentro "Scarica": il pagamento
 * arriva comunque, e vederlo dall'inizio evita che sembri una sorpresa a
 * lavoro finito.
 */

const S = {
  attivo: "#009CDD",
  spento: "#B7DAE9",
  linea: "#def0f9",
  bianco: "#ffffff",
  titolo: "#182E4F",
  grigio: "#5D6D84",
};

export const PROCESS_STEPS = ["Carica", "Configura", "Elabora", "Acquista", "Scarica"];

function Spunta({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/**
 * @param {{ step: number, isMobile?: boolean, inline?: boolean }} props step da 1 a 5;
 *   con `inline` la barra vive DENTRO la top bar (niente sfondo, bordo o
 *   posizione fissa: e' la top bar a occuparsene), e su telefono mostra solo
 *   i cerchi, con il nome del passo corrente nel titolo accessibile.
 */
export default function ProcessSteps({ step, isMobile = false, inline = false }) {
  const corrente = Math.min(Math.max(1, Number(step) || 1), PROCESS_STEPS.length);
  const diametro = inline ? (isMobile ? 22 : 24) : isMobile ? 24 : 28;
  const etichette = !isMobile;

  const lista = (
      <ol
        aria-label={inline ? `Passo ${corrente} di ${PROCESS_STEPS.length}: ${PROCESS_STEPS[corrente - 1]}` : undefined}
        style={{ display: "flex", alignItems: "center", listStyle: "none", margin: "0 auto", padding: 0, maxWidth: inline ? 520 : 720, width: "100%" }}
      >
        {PROCESS_STEPS.map((nome, i) => {
          const n = i + 1;
          const fatto = n < corrente;
          const attivo = n === corrente;
          const acceso = fatto || attivo;

          return (
            <Fragment key={nome}>
              {i > 0 && (
                <li
                  aria-hidden="true"
                  style={{
                    flex: 1,
                    height: 2,
                    minWidth: inline ? 8 : 12,
                    margin: "0 6px",
                    marginBottom: etichette ? (inline ? 16 : 18) : 0,
                    borderRadius: 2,
                    background: n <= corrente ? S.attivo : S.linea,
                    transition: "background .35s ease",
                  }}
                />
              )}
              <li
                aria-current={attivo ? "step" : undefined}
                title={inline ? nome : undefined}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: inline ? 4 : 6, flexShrink: 0 }}
              >
                <span
                  style={{
                    width: diametro,
                    height: diametro,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                    fontFamily: F.body,
                    fontSize: isMobile ? 11 : inline ? 11 : 12,
                    fontWeight: 700,
                    background: acceso ? S.attivo : S.bianco,
                    color: acceso ? S.bianco : S.spento,
                    border: `2px solid ${acceso ? S.attivo : S.spento}`,
                    // solo il passo corrente ha l'anello: distingue "ci sono
                    // adesso" da "l'ho gia' fatto" senza affidarsi al colore
                    boxShadow: attivo ? "0 0 0 4px rgba(0,156,221,0.18)" : undefined,
                    transition: "background .35s ease, border-color .35s ease",
                  }}
                >
                  {fatto ? <Spunta size={isMobile ? 11 : 13} /> : n}
                </span>
                {etichette && (
                  <span
                    style={{
                      fontFamily: F.body,
                      fontSize: inline ? 10 : 11,
                      fontWeight: attivo ? 700 : 500,
                      color: attivo ? S.titolo : S.grigio,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {nome}
                  </span>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
  );

  if (inline) {
    return (
      <nav aria-label="Avanzamento dell'anonimizzazione" style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minWidth: 0 }}>
        {lista}
      </nav>
    );
  }

  return (
    <nav
      aria-label="Avanzamento dell'anonimizzazione"
      style={{
        background: S.bianco,
        borderBottom: `1px solid ${S.linea}`,
        padding: isMobile ? "10px 16px 12px" : "12px 32px",
        position: "sticky",
        // sotto la top bar (che sta a 56px e ha zIndex 50): le due barre
        // scorrono insieme e restano visibili in ogni fase, elaborazione
        // compresa.
        top: isMobile ? 0 : 56,
        zIndex: 49,
      }}
    >
      {lista}

      {isMobile && (
        // Su telefono cinque etichette non ci stanno: si mostra solo quella
        // del passo corrente, che e' l'unica che serve davvero.
        <p style={{ margin: "8px 0 0", textAlign: "center", fontFamily: F.body, fontSize: 12, color: S.grigio }}>
          Passo {corrente} di {PROCESS_STEPS.length} · <strong style={{ color: S.titolo }}>{PROCESS_STEPS[corrente - 1]}</strong>
        </p>
      )}
    </nav>
  );
}
